/**
 * EncounterListContext
 *
 * Provides state management and data fetching for the EncounterList component.
 * Handles both standalone (Appointments) and connected (Encounters) modes.
 */

import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { appointmentService } from '@/services/appointment';
import { encounterService } from '@/services/encounter';
import {
  EncounterListConfig,
  EncounterListContextValue,
  EncounterListFilters,
  UnifiedEncounterItem,
  PaginationState,
  ActiveFilter,
  DEFAULT_PAGE_SIZE,
  STATUS_OPTIONS,
  STANDALONE_STATUS_OPTIONS,
  TYPE_OPTIONS,
  RECORDING_STATUS_OPTIONS,
  TRANSCRIPTION_STATUS_OPTIONS,
  MATCH_STATUS_OPTIONS,
} from './types';
import { appointmentDateParams } from './utils/dateRangeParams';
import { adaptAppointments } from './adapters/appointmentAdapter';
import { adaptEncounters } from './adapters/encounterAdapter';
import { usePagination } from './hooks/usePagination';
import { useMediaQuery } from './hooks/useMediaQuery';

// ============================================================================
// Context
// ============================================================================

const EncounterListContext = createContext<EncounterListContextValue | null>(null);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format date for API
 */
function formatDateForAPI(date: Date | undefined): string | undefined {
  if (!date) return undefined;
  return format(date, 'yyyy-MM-dd');
}

/**
 * Get active filters for display
 */
function getActiveFilters(filters: EncounterListFilters): ActiveFilter[] {
  const active: ActiveFilter[] = [];

  if (filters.search) {
    active.push({
      key: 'search',
      label: 'Search',
      displayValue: filters.search,
      value: filters.search,
    });
  }

  if (filters.status?.length) {
    const labels = filters.status
      .map((s) => [...STATUS_OPTIONS, ...STANDALONE_STATUS_OPTIONS].find((o) => o.value === s)?.label || s)
      .join(', ');
    active.push({
      key: 'status',
      label: 'Status',
      displayValue: labels,
      value: filters.status,
    });
  }

  if (filters.type?.length) {
    const labels = filters.type
      .map((t) => TYPE_OPTIONS.find((o) => o.value === t)?.label || t)
      .join(', ');
    active.push({
      key: 'type',
      label: 'Type',
      displayValue: labels,
      value: filters.type,
    });
  }

  if (filters.recordingStatus?.length) {
    const labels = filters.recordingStatus
      .map((s) => RECORDING_STATUS_OPTIONS.find((o) => o.value === s)?.label || s)
      .join(', ');
    active.push({
      key: 'recordingStatus',
      label: 'Recording',
      displayValue: labels,
      value: filters.recordingStatus,
    });
  }

  if (filters.transcriptionStatus?.length) {
    const labels = filters.transcriptionStatus
      .map((s) => TRANSCRIPTION_STATUS_OPTIONS.find((o) => o.value === s)?.label || s)
      .join(', ');
    active.push({
      key: 'transcriptionStatus',
      label: 'Transcription',
      displayValue: labels,
      value: filters.transcriptionStatus,
    });
  }

  if (filters.dateFrom || filters.dateTo) {
    const fromStr = filters.dateFrom ? format(filters.dateFrom, 'MMM d, yyyy') : '';
    const toStr = filters.dateTo ? format(filters.dateTo, 'MMM d, yyyy') : '';
    const displayValue = fromStr && toStr ? `${fromStr} - ${toStr}` : fromStr || toStr;
    active.push({
      key: 'dateFrom',
      label: 'Date',
      displayValue,
      value: { from: filters.dateFrom, to: filters.dateTo },
    });
  }

  if (filters.matchStatus?.length) {
    const labels = filters.matchStatus
      .map((s) => MATCH_STATUS_OPTIONS.find((o) => o.value === s)?.label || s)
      .join(', ');
    active.push({
      key: 'matchStatus',
      label: 'Match Status',
      displayValue: labels,
      value: filters.matchStatus,
    });
  }

  if (filters.providerId) {
    active.push({
      key: 'providerId',
      label: 'Provider',
      displayValue: 'Selected',
      value: filters.providerId,
    });
  }

  return active;
}

// ============================================================================
// Provider Component
// ============================================================================

interface EncounterListProviderProps {
  config: EncounterListConfig;
  children: React.ReactNode;
}

export function EncounterListProvider({ config, children }: EncounterListProviderProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  // State
  const [items, setItems] = useState<UnifiedEncounterItem[]>([]);
  const [filters, setFiltersState] = useState<EncounterListFilters>({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  // Pagination
  const {
    pagination,
    setPage,
    setLimit,
    setTotal,
    reset: resetPagination,
  } = usePagination({
    defaultLimit: config.defaultPageSize || DEFAULT_PAGE_SIZE,
    pageSizeOptions: config.pageSizeOptions,
  });

  // ============================================================================
  // Data Fetching
  // ============================================================================

  const fetchData = useCallback(
    async (isLoadMore = false) => {
      try {
        if (isLoadMore) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }
        setError(null);

        if (config.mode === 'standalone') {
          // Fetch appointments
          const response = await appointmentService.getAppointments({
            page: isLoadMore ? pagination.page + 1 : pagination.page,
            limit: pagination.limit,
            // Every selected status and type, filtered on the server so totals and paging agree.
            listStatus: filters.status?.length ? filters.status.join(',') : undefined,
            type: filters.type?.length ? filters.type.join(',') : undefined,
            providerId: filters.providerId,
            patientId: filters.patientId,
            ...appointmentDateParams(filters.dateFrom, filters.dateTo),
            // search: filters.search, // Will be added in backend update
          });

          const adaptedItems = adaptAppointments(response.appointments);

          // Client-side search filter (until backend supports it)
          let filteredItems = adaptedItems;
          if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filteredItems = adaptedItems.filter(
              (item) =>
                item.patientName.toLowerCase().includes(searchLower) ||
                item.type.toLowerCase().includes(searchLower) ||
                item.location?.toLowerCase().includes(searchLower) ||
                item.chiefComplaint?.toLowerCase().includes(searchLower) ||
                item.providerName?.toLowerCase().includes(searchLower)
            );
          }

          // Client-side recording status filter
          if (filters.recordingStatus?.length) {
            filteredItems = filteredItems.filter(
              (item) =>
                item.recordingStatus && filters.recordingStatus!.includes(item.recordingStatus)
            );
          }

          // Client-side transcription status filter
          if (filters.transcriptionStatus?.length) {
            filteredItems = filteredItems.filter(
              (item) =>
                item.transcriptionStatus &&
                filters.transcriptionStatus!.includes(item.transcriptionStatus)
            );
          }

          if (isLoadMore) {
            setItems((prev) => [...prev, ...filteredItems]);
          } else {
            setItems(filteredItems);
          }

          setTotal(response.pagination.total);
          setHasMore(pagination.page < response.pagination.pages);
        } else {
          // Fetch encounters (connected mode)
          const response = await encounterService.getEncounters({
            matchStatus: filters.matchStatus?.[0] as 'matched' | 'unmatched' | 'pending' | undefined,
            dateFrom: formatDateForAPI(filters.dateFrom),
            dateTo: formatDateForAPI(filters.dateTo),
            limit: pagination.limit,
            page: isLoadMore ? pagination.page + 1 : pagination.page,
            search: filters.search, // Server-side search
            // Server-side, so pages and totals reflect the filter
            status: filters.status?.length ? filters.status.join(',') : undefined,
          });

          let adaptedItems = adaptEncounters(response.encounters);

          // Client-side type filter (until backend supports it)
          if (filters.type?.length) {
            adaptedItems = adaptedItems.filter((item) =>
              filters.type!.includes(item.type)
            );
          }

          if (isLoadMore) {
            setItems((prev) => [...prev, ...adaptedItems]);
          } else {
            setItems(adaptedItems);
          }

          // Use server pagination
          setTotal(response.pagination.total);
          setHasMore(pagination.page < response.pagination.pages);
        }
      } catch (err) {
        console.error('[EncounterList] Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [config.mode, pagination.page, pagination.limit, filters, setTotal]
  );

  // Initial fetch and refetch on filter/pagination changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ============================================================================
  // Actions
  // ============================================================================

  const setFilters = useCallback(
    (newFilters: Partial<EncounterListFilters>) => {
      setFiltersState((prev) => ({ ...prev, ...newFilters }));
      setPage(1); // Reset to first page when filters change
    },
    [setPage]
  );

  const clearFilters = useCallback(() => {
    setFiltersState({});
    resetPagination();
  }, [resetPagination]);

  const removeFilter = useCallback(
    (key: keyof EncounterListFilters) => {
      setFiltersState((prev) => {
        const newFilters = { ...prev };
        delete newFilters[key];
        // Also clear dateTo if dateFrom is removed
        if (key === 'dateFrom') {
          delete newFilters.dateTo;
        }
        return newFilters;
      });
      setPage(1);
    },
    [setPage]
  );

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      setPage(pagination.page + 1);
      fetchData(true);
    }
  }, [loadingMore, hasMore, pagination.page, setPage, fetchData]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // ============================================================================
  // Active Filters
  // ============================================================================

  const activeFilters = useMemo(() => getActiveFilters(filters), [filters]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const contextValue: EncounterListContextValue = useMemo(
    () => ({
      // State
      items,
      filters,
      pagination,
      loading,
      loadingMore,
      error,
      hasMore,
      // Config
      config,
      isMobile,
      activeFilters,
      // Actions
      setFilters,
      clearFilters,
      removeFilter,
      setPage,
      setLimit,
      loadMore,
      refresh,
    }),
    [
      items,
      filters,
      pagination,
      loading,
      loadingMore,
      error,
      hasMore,
      config,
      isMobile,
      activeFilters,
      setFilters,
      clearFilters,
      removeFilter,
      setPage,
      setLimit,
      loadMore,
      refresh,
    ]
  );

  return (
    <EncounterListContext.Provider value={contextValue}>
      {children}
    </EncounterListContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export function useEncounterList(): EncounterListContextValue {
  const context = useContext(EncounterListContext);
  if (!context) {
    throw new Error('useEncounterList must be used within an EncounterListProvider');
  }
  return context;
}

export default EncounterListContext;
